import React, { useState, useEffect, useRef } from 'react'
import { PageType, KnowledgePageType } from '../constants'
import { Badge } from 'antd'
import { useNavigate, useLocation } from 'react-router-dom'
import { queryDomain, domainShowRed, } from '@/api/dictionary'
import i18n from '@/locales'
import Icon, { PlusOutlined } from '@ant-design/icons'
import { permissionAuth } from '@/utils/permission'
import useStore from '@/store'

const microData = (window as any).microApp?.getData() || {}

export const path = {
  [PageType.View]: '/dict-view',
  [PageType.Edit]: '/dict-edit',
  [PageType.Audit]: '/dict-audit',
  [PageType.Quote]: '/quote',
}

interface IProps {
  includes?: PageType[],
  knowledgeIncludes?: KnowledgePageType[],
}
type MenuItem = {
  key: string;
  label: string | JSX.Element;
  id: any;
  children: MenuItem[];  // 子菜单的类型是 `MenuItem` 的数组
  moduleType: number;
};
const throwParents = (data: MenuItem[]): MenuItem[] => {
  const result: MenuItem[] = [];

  // 遍历每个菜单项
  data.forEach(item => {
    // 如果当前项有 children，递归处理
    if (item.children && item.children.length > 0) {
      result.push(...item.children);
    }
  });

  return result;
};

const generateMenu = (list: any[], type: PageType) => {
  if (!Array.isArray(list)) return [];

  const base = {
    url: path[type],
    menutype: 'dict',
  };

  const createBadgeLabel = (label: string, showRed: boolean) =>
    showRed && type === PageType.Audit ? <Badge dot>{label}</Badge> : label;

  const createMenuItem = (item: any, index: number) => {
    const { dictList, domainName, showRed, id } = item;
    const key = `${type}-${index}`;
    const label = createBadgeLabel(domainName, showRed);

    // 生成子菜单项
    const children = (dictList || [])?.map((dict: any, subIndex: number) => {
      const { dictName, showRed } = dict;
      const dictKey = `${key}-${subIndex}`;
      const dictLabel = createBadgeLabel(dictName, showRed);

      return {
        label: dictLabel,
        key: dictKey,
        domainid: id,
        dictname: dictName,
        isControlled: domainName.includes("受控术语") ? 1 : "",
        isOMOP: domainName.toUpperCase().includes("OMOP") ? 1 : "",
        isWHODrugB3: dictName.toUpperCase().includes('WHODRUGB3') ? 1 : "",
        isWHODrugC3: dictName.toUpperCase().includes('WHODRUGC3') ? 1 : "",
        isMedDRA: dictName.toUpperCase().includes('MEDDRA') ? 1 : "",
        moduleType: 1,
        ...base,
      };
    }) || [];

    // 如果是审计类型并有权限，增加“创建版本”菜单项
    if (type === PageType.Audit && permissionAuth('CREATE_VERSION_DICT_AUDIT')) {
      children.push({
        label: i18n.t('ikg.create'),
        key: `${key}-create-version`,
        domainid: id,
        menutype: 'create-version',
        icon: <Icon component={PlusOutlined as any} />,
        moduleType: 1,
      });
    }

    return {
      key,
      label,
      id,
      children: children.length > 0 ? children : null,
      moduleType: 1,
    };
  };

  return list.map(createMenuItem);
};


export const generateKonwledgeMenu = (list: any[], type: KnowledgePageType) => {
  if (!Array.isArray(list)) return [];

  //TODO url
  let base = {
    url: path[type],
    menutype: 'knowledge',
  };

  let knowledgeOutermostRed = false;

  const result = list.map((item, index) => {
    const { dictList = [], domainName: dictname, showRed, id: dictid } = item;
    const key = `${type * 10}-${index}`;
    let label = dictname;

    // 如果是审核类型且需要显示红点
    if (showRed && type === KnowledgePageType.Audit) {
      label = <Badge dot>{dictname}</Badge>;
      knowledgeOutermostRed = true;
    }

    // 子菜单项生成
    const children = (dictList || [])?.map((dict, subIndex) => {
      const { dictName: subDictName, showRed: subShowRed, dictType } = dict;
      const dictKey = `${key}-${subIndex}`;
      let subLabel: React.ReactNode = subDictName;
      if (dictType === 2 && type === 3) {
        // 知识库 审核 
        base = { menutype: 'auditKnowledge-base', url: '/dict-knowledge-audit' }
      } else if (dictType === 2 && type === 1) {
        // 知识库 查看
        base = { menutype: 'auditKnowledge-base', url: '/dict-knowledge-view' }
      }
      // 审核类型且子菜单需要显示红点
      if (subShowRed && type === KnowledgePageType.Audit) {
        subLabel = <Badge dot>{subDictName}</Badge>;
        knowledgeOutermostRed = true;
      }

      return {
        label: subLabel,
        key: dictKey,
        dictid,
        dictname: subDictName,
        childfirstele: null, // 可以根据业务需求调整
        moduleType: 2,
        ...base,
      };
    });

    return {
      key,
      label,
      dictid,
      dictname,
      children: children.length > 0 ? children : null,
      moduleType: 2,
    };
  });

  return { result, knowledgeOutermostRed };
};

export const generaStandardMenu = (list: any[], type: KnowledgePageType) => {
  if (!Array.isArray(list)) return []
  const base = {
    url: "/standard",
    menutype: 'standard',
  }
  const result: any = []
  list.forEach((item, index) => {
    const { domainName, showRed, domainId, dictList, id } = item
    const key = `${type * 11}-${index}`
    let label = domainName
    const children = dictList
      ? dictList.map((dict: any, subIndex: number) => {
        const { dictName, showRed, domainId, child = [] } = dict
        const dictKey = `${key}-${subIndex}`
        let label: React.ReactNode = dictName
        return {
          label,
          domainId: id,
          dictName,
          key: dictKey,
          childfirstele: child[0] || null,
          moduleType: 3,
          ...base,
        }
      })
      : []
    result.push({
      key,
      label,
      domainId,
      domainName,
      children: children.length > 0 ? children : null,
      moduleType: 3,
    })
  })
  return result
}


const useDictMenu = (props?: IProps) => {
  const location = useLocation()
  const { includes, knowledgeIncludes = [] } = props || {}
  const includeTypes =
    includes ||
    Object.values(PageType).filter((value) => typeof value === 'number')
  const navigate = useNavigate()
  const { domainRedFlag } = useStore() as any
  const [reload, setReload] = useState(false)
  const [viewDicts, setViewDicts] = useState<any>([])
  const [editDicts, setEditDicts] = useState<any>([])
  const [auditDicts, setAuditDicts] = useState<any>([])
  const [quoteDicts, setQuoteDicts] = useState<any>([])

  const [viewKnowledge, setViewKnowledge] = useState<any>([])
  const [editKnowledge, setEditKnowledge] = useState<any>([])
  const [auditKnowledge, setAuditKnowledge] = useState<any>([])
  const [standard, setStandard] = useState<any>([]);
  const [knowledgeOutermostRed, setKnowledgeOutermostRed] = useState<boolean>(false)

  const fetchDicts = async (type: PageType) => {
    let postParams = {
      type: type === PageType.Quote ? PageType.View : type,
    }
    let needThrowParent = false;
    if (microData.domainParams) {
      const { dictName, partialDictName, ...microDataDomainParams } = microData.domainParams;
      if (partialDictName) {
        needThrowParent = true;
      }
      postParams = {
        ...postParams,
        ...microDataDomainParams,
        ...(partialDictName ? { dictName: partialDictName } : {})
      }
    }
    const res = await queryDomain(postParams) as any
    const dictRes = res.find(item => item.moduleType === 1).domainList
    const dictresult = generateMenu(dictRes, type)
    const knowRes = res.find(item => item.moduleType === 2).domainList
    const knowResult = generateKonwledgeMenu(knowRes, type as any) as any
    const standRes = res.find(item => item.moduleType === 3).domainList
    const standResult = generaStandardMenu(standRes, type as any) as any

    switch (type) {
      case PageType.View:
        setViewDicts(dictresult)
        setStandard(standResult)
        setViewKnowledge(knowResult.result)
        break
      case PageType.Edit:
        setEditDicts(dictresult)
        setEditKnowledge(knowResult.result)
        break
      case PageType.Audit:
        setAuditDicts(dictresult)
        setAuditKnowledge(knowResult.result)
        setKnowledgeOutermostRed(knowResult?.knowledgeOutermostRed)
        break
      case PageType.Quote:
        setQuoteDicts(needThrowParent ? throwParents(dictresult) : dictresult)
        break
    }
  }

  const initDicts = (types?: PageType[]) => {
    const _types = types || includeTypes
    _types.forEach((t) => {
      fetchDicts(t as PageType)
    })
  }

  useEffect(() => {
    initDicts()
  }, [reload])


  const onNavigate = (props: any) => {
    let {
      domainid,
      dictname,
      domainname = '',
      url,
      dictid,
      dictcode = '',
      childfirstele,
      isControlled,
      isOMOP,
      isWHODrugB3,
      isWHODrugC3,
      isMedDRA,
    } = props;
    if (domainid) {
      const params = new URLSearchParams();
      if (domainid) params.append('domainId', domainid);
      if (domainname) params.append('domainName', domainname);
      if (dictname) params.append('dictName', dictname);
      if (isControlled) params.append('isControlled', isControlled);
      if (isOMOP) params.append('isOMOP', isOMOP);
      if (isWHODrugB3) params.append('isWHODrugB3', isWHODrugB3);
      if (isWHODrugC3) params.append('isWHODrugC3', isWHODrugC3);
      if (isMedDRA) params.append('isMedDRA', isMedDRA);
      params.append('moduleType', props.moduleType);
      const finalUrl = `${url}?${params.toString()}`;
      navigate(finalUrl, {
        replace: location.pathname === url,
      });
    } else {
      // 知识库的第一级目录和第二级目录是后端写死的
      dictname = childfirstele?.dictName || dictname || ''
      dictid = dictid || childfirstele?.dictId || ''
      dictcode = dictcode || childfirstele?.dictCode || ''
      navigate(
        `${url}?dictId=${dictid}&dictName=${dictname}&dictCode=${dictcode}&kowledge=1&domainId=${dictid}&moduleType=2`,
        {
          replace: location.pathname === url,
        })
    }
  }

  const goto = (props) => {
    const { url, domainId, dictName, } = props;
    navigate(
      `${url}?domainId=${domainId}&dictName=${dictName}`,
      {
        replace: location.pathname === url,
      })
  }

  const gotoKnowledgeBase = (props) => {
    const { url, dictid, dictname, } = props;
    navigate(
      `${url}?domainId=${dictid}&dictName=${dictname}`,
      {
        replace: location.pathname === url,
      })
  }
  return {
    goto,
    standard,
    viewDicts,
    editDicts,
    auditDicts,
    quoteDicts,
    viewKnowledge,
    editKnowledge,
    auditKnowledge,
    knowledgeOutermostRed,
    fetchDicts,
    reload() {
      setReload(!reload)
    },
    onNavigate,
    gotoKnowledgeBase
  }
}

export const useDomainShowRed = () => {
  const { domainRedFlag } = useStore() as any
  const [showRedMap, setShowRedMap] = useState<any>([])
  const timerRef = useRef<any>(null)
  const getIsShowRed = () => {
    domainShowRed().then((res) => {
      setShowRedMap(res)
    })
  }

  const initTimer = () => {
    timerRef.current = setInterval(() => {
      getIsShowRed()
    }, 10 * 1000)
  }

  useEffect(() => {
    getIsShowRed()
  }, [domainRedFlag])

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }, [])
  return {
    showRedMap,
  }
}

export default useDictMenu
